import type { ProfileCardSpan } from '@/components/common/ProfileCardGrid';
import type { StartupProfileData } from '@/features/profiles/api/startup-profile.api';
import type { StartupSectionId } from '@/features/profiles/modules/startup/startup-sections';
import { STARTUP_SECTIONS } from '@/features/profiles/modules/startup/startup-sections';
import { STARTUP_PROFILE_STRINGS } from '@/features/profiles/modules/startup/startup-profile.strings';
import type { PublicStartupProfile } from './api/shared-profile.api';

/**
 * The redacted public DTO in the shape the startup profile cards read, so the
 * shared page renders the very same header and cards (FR-180). Only fields the
 * share link already exposes are mapped; the rest stay empty and are hidden.
 * Pure.
 */
export function toStartupCardData(profile: PublicStartupProfile): StartupProfileData {
  return {
    ...profile,
    id: '',
    entityId: '',
    // The deck's storage key is not public; a label stands in for its name.
    pitchDeckUrl: profile.hasPitchDeck ? STARTUP_PROFILE_STRINGS.PITCH_DECK : null,
    sectors: profile.sectors.map((row) => ({ ...row, sectorId: 0 })),
    marketModels: profile.marketModels,
    investorTypesSought: profile.investorTypes,
    foundingTeam: profile.foundingTeam,
    founderFemale: null,
    founderUnderrepresented: null,
    completion: undefined,
  };
}

function anyValue(values: readonly unknown[]): boolean {
  return values.some(
    (value) => value !== null && value !== undefined && value !== '' && value !== false,
  );
}

/** Whether each card has anything to show; an empty card is hidden on the shared page (FR-180). Pure. */
export function visibleSharedCards(p: PublicStartupProfile): Record<StartupSectionId, boolean> {
  return {
    company:
      anyValue([
        p.fundingStage,
        p.yearFounded,
        p.employeeCount,
        p.aiClassification,
        p.recentMilestone,
      ]) || p.marketModels.length > 0,
    raise:
      anyValue([
        p.financeSoughtLow,
        p.financeSoughtHigh,
        p.instrument,
        p.valuationOrCap,
        p.targetCloseDate,
        p.hasLeadInvestor,
        p.useOfFunds,
      ]) || p.investorTypes.length > 0,
    team: p.foundingTeam.length > 0,
    pitch: p.hasPitchDeck || Boolean(p.pitchVideoUrl),
    financials: anyValue([
      p.totalRevenueRange,
      p.arr,
      p.amountRaisedToDate,
      p.nondilutiveFunding,
      p.revenueModel,
      p.revenueGrowthRate,
      p.payingCustomers,
    ]),
  };
}

// The cards pair up two to a row; a card whose partner is hidden takes the row.
const PARTNER: Partial<Record<StartupSectionId, StartupSectionId>> = {
  company: 'raise',
  raise: 'company',
  team: 'pitch',
  pitch: 'team',
};

/** A visible card's width: its own span beside a visible partner, otherwise the full row. Pure. */
export function sharedCardSpan(
  id: StartupSectionId,
  visible: Record<StartupSectionId, boolean>,
): ProfileCardSpan {
  const partner = PARTNER[id];
  return partner && visible[partner] ? STARTUP_SECTIONS[id].span : 12;
}
